import type { PhotoAnnotation } from './annotation';
import { newId } from './ids';
import { V2_SCHEMA_VERSION } from './inspection';

// Evidence domain — photos are independent records, never a page-local image array.
// One photo may be used by an area, by items and by findings at the same time: the record
// holds the CAPTURE LOCATION (where it was taken/imported — immutable), while items and
// findings hold their own link lists (OWNER-CONTRACT §6.2 three-layer photo model).
export type EvidenceSource = 'camera' | 'photoLibrary';

// Album ledger (OWNER-CONTRACT §6.1). Fail-closed reading: only 'saved' claims the album has a
// copy. 'notRequired' = imported from the library (never re-saved); 'unavailable' = the
// platform has no system album (browser); 'failed' = the write was attempted and refused.
export type AlbumSaveState = 'pending' | 'saved' | 'failed' | 'unavailable' | 'notRequired';

export interface V2Evidence {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  // Capture location area, or null for a photo imported into the inspection's PHOTO POOL (Owner
  // task book §9): pool photos belong to the inspection and are linked to items / findings later.
  areaId: string | null;
  // Capture location item (null when taken at area level). Never changed by later linking.
  itemId: string | null;
  source: EvidenceSource;
  // v2media://<id> — resolved by src/state/evidenceStore.ts against the V2 media store.
  localUri: string;
  mimeType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  albumSaveState: AlbumSaveState;
  albumSaveDetail: string;
  caption: string;
  // 2026-10-19 (Owner item 31): the photo-checklist shot this photo was taken for (src/domain/photoGuide.ts),
  // or null. Optional on disk → null.
  shot?: string | null;
  // 2026-10-19 (Owner item 22): arrows / circles / boxes drawn on the photo. Optional on disk → [].
  annotations?: PhotoAnnotation[];
  createdAt: number;
  updatedAt: number;
}

export const ALBUM_SAVE_LABELS: Record<AlbumSaveState, string> = {
  pending: '相册保存中',
  saved: '已存系统相册',
  failed: '相册保存失败',
  unavailable: '此平台无系统相册',
  notRequired: '相册原图（导入）'
};

export const EVIDENCE_SOURCE_LABELS: Record<EvidenceSource, string> = {
  camera: '拍照',
  photoLibrary: '相册导入'
};

export const MEDIA_URI_PREFIX = 'v2media://';

export function mediaUriFor(evidenceId: string): string {
  return `${MEDIA_URI_PREFIX}${evidenceId}`;
}

export function isMediaUri(uri: string): boolean {
  return uri.startsWith(MEDIA_URI_PREFIX);
}

export interface NewEvidenceInput {
  inspectionId: string;
  areaId: string | null;
  itemId: string | null;
  source: EvidenceSource;
  mimeType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  shot?: string | null;
}

export function createEvidence(input: NewEvidenceInput, now: number): V2Evidence {
  const id = newId('ev');
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id,
    inspectionId: input.inspectionId,
    areaId: input.areaId,
    itemId: input.itemId,
    source: input.source,
    localUri: mediaUriFor(id),
    mimeType: input.mimeType,
    byteSize: input.byteSize,
    width: input.width,
    height: input.height,
    // Camera photos start 'pending' — the album write is requested before this record is even
    // persisted (src/state/captureFlow.ts). Library imports never owe an album copy.
    albumSaveState: input.source === 'camera' ? 'pending' : 'notRequired',
    albumSaveDetail: input.source === 'camera' ? '' : '从相册导入，原图已在相册，不再另存。',
    caption: '',
    ...(input.shot ? { shot: input.shot } : {}),
    createdAt: now,
    updatedAt: now
  };
}

export function withAlbumState(
  evidence: V2Evidence,
  state: AlbumSaveState,
  detail: string,
  now: number
): V2Evidence {
  return { ...evidence, albumSaveState: state, albumSaveDetail: detail, updatedAt: now };
}

export function withCaption(evidence: V2Evidence, caption: string, now: number): V2Evidence {
  return { ...evidence, caption: caption.trim(), updatedAt: now };
}

export function withAnnotations(evidence: V2Evidence, annotations: PhotoAnnotation[], now: number): V2Evidence {
  return { ...evidence, annotations, updatedAt: now };
}

export function albumCopyIsSafe(evidence: V2Evidence): boolean {
  return evidence.albumSaveState === 'saved' || evidence.albumSaveState === 'notRequired';
}
